import { apiCall } from "@/lib/api";
import type { ExecutiveData, ManagerData } from "@/hooks/useDashboardData";

// ─── Shared CSV helpers ───────────────────────────────────────────────────────
function csvEscape(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
function csvRow(cells: Array<string | number | null | undefined>): string {
  return cells.map(csvEscape).join(",");
}

// ─── PDF section pickers (used only by the PDF button — PDF stays summary) ───
export type ExecSectionKey = "readiness" | "uplift" | "themes" | "competencies" | "topPerformers" | "highlights";
export type ManagerSectionKey = "funnel" | "actionQueues" | "completionBySim" | "timeToComplete" | "dropOff";

export const EXEC_SECTIONS: Array<{ key: ExecSectionKey; label: string }> = [
  { key: "readiness", label: "Readiness mix" },
  { key: "uplift", label: "Avg uplift" },
  { key: "themes", label: "AI themes" },
  { key: "competencies", label: "Competency profile" },
  { key: "topPerformers", label: "Top 10 performers" },
  { key: "highlights", label: "Standout / concern cohorts" },
];

export const MANAGER_SECTIONS: Array<{ key: ManagerSectionKey; label: string }> = [
  { key: "funnel", label: "Funnel" },
  { key: "actionQueues", label: "Action queues" },
  { key: "completionBySim", label: "Per-sim completion" },
  { key: "timeToComplete", label: "Time-to-complete" },
  { key: "dropOff", label: "Drop-off map" },
];

// ─── Raw rows (the CSV path) ──────────────────────────────────────────────────
export interface RawRow {
  userId: string;
  fullName: string;
  email: string;
  clientName: string;
  projectName: string;
  cohortName: string;
  simId: string;
  simCode: string;
  simTitle: string;
  competencyName: string | null;
  assignedAt: string;
  attemptId: string | null;
  attemptNo: number | null;
  status: string;
  totalScore: number | null;
  decisions: number | null;
  scoreNormalised: number | null;
  finalOutcomeBand: string | null;
  startedAt: string | null;
  endedAt: string | null;
  timeMinutes: number | null;
  lastActivity: string | null;
}

export interface RawRowsResponse {
  clientName: string;
  period: string;
  generatedAt: string;
  rows: RawRow[];
}

// Column catalog — defines which fields can appear in the CSV, in what
// order, and the label that becomes the column header. The frontend picker
// is built from this list, so adding a new column is one entry.
export interface RawColumn {
  key: keyof RawRow;
  label: string;
  group: "Learner" | "Cohort" | "Sim" | "Attempt" | "Timing";
  /** Ticked by default — keeps the picker scannable when first opened. */
  defaultOn: boolean;
}

export const RAW_COLUMNS: RawColumn[] = [
  { key: "fullName",         label: "Learner name",     group: "Learner", defaultOn: true },
  { key: "email",            label: "Email",            group: "Learner", defaultOn: true },
  { key: "userId",           label: "User ID",          group: "Learner", defaultOn: false },
  { key: "clientName",       label: "Client",           group: "Cohort",  defaultOn: false },
  { key: "projectName",      label: "Project",          group: "Cohort",  defaultOn: true },
  { key: "cohortName",       label: "Cohort",           group: "Cohort",  defaultOn: true },
  { key: "simCode",          label: "Sim code",         group: "Sim",     defaultOn: false },
  { key: "simTitle",         label: "Sim title",        group: "Sim",     defaultOn: true },
  { key: "competencyName",   label: "Competency",       group: "Sim",     defaultOn: true },
  { key: "attemptNo",        label: "Attempt #",        group: "Attempt", defaultOn: true },
  { key: "status",           label: "Status",           group: "Attempt", defaultOn: true },
  { key: "totalScore",       label: "Total score",      group: "Attempt", defaultOn: true },
  { key: "decisions",        label: "Decisions",        group: "Attempt", defaultOn: true },
  { key: "scoreNormalised",  label: "Score / 10",       group: "Attempt", defaultOn: true },
  { key: "finalOutcomeBand", label: "Band",             group: "Attempt", defaultOn: true },
  { key: "assignedAt",       label: "Assigned at",      group: "Timing",  defaultOn: false },
  { key: "startedAt",        label: "Started at",       group: "Timing",  defaultOn: true },
  { key: "endedAt",          label: "Ended at",         group: "Timing",  defaultOn: true },
  { key: "timeMinutes",      label: "Time (min)",       group: "Timing",  defaultOn: true },
  { key: "lastActivity",     label: "Last activity",    group: "Timing",  defaultOn: false },
];

export type ColumnPick = Record<string, boolean>;

export function defaultColumnPick(): ColumnPick {
  return Object.fromEntries(RAW_COLUMNS.map((c) => [c.key, c.defaultOn]));
}

function formatCell(row: RawRow, key: keyof RawRow): string | number | null {
  const v = row[key];
  if (v === null || v === undefined) return "";
  // Timestamps: leave as ISO so spreadsheets can parse them; the user can
  // format on their side without losing precision.
  return v as string | number;
}

/** Fetch the raw rows server-side, role-gated. */
export async function fetchRawRows(clientId: string | null, period: string): Promise<RawRowsResponse> {
  const body: Record<string, unknown> = { period };
  if (clientId !== null && clientId !== "" && !clientId.startsWith("__")) body["clientId"] = clientId;
  return apiCall<RawRowsResponse>("/libraryDashboardRawRows", { method: "POST", body, timeoutMs: 120_000 });
}

/** Build a CSV string from raw rows + the user's column picks. */
export function rawRowsToCsv(resp: RawRowsResponse, pick: ColumnPick): string {
  const picked = RAW_COLUMNS.filter((c) => pick[c.key] === true);
  if (picked.length === 0) {
    return `# No columns selected — pick at least one and try again.\n`;
  }
  const meta = [
    `# NeuSim dashboard raw data`,
    `# Client,${csvEscape(resp.clientName)}`,
    `# Period,${csvEscape(resp.period)}`,
    `# Generated,${csvEscape(resp.generatedAt)}`,
    `# Rows,${resp.rows.length}`,
    "",
  ].join("\n");
  const header = csvRow(picked.map((c) => c.label));
  const lines = resp.rows.map((row) => csvRow(picked.map((c) => formatCell(row, c.key))));
  return meta + header + "\n" + lines.join("\n") + (lines.length > 0 ? "\n" : "");
}

// ─── Legacy summary CSVs (kept ONLY so existing PDF-section pickers still
// compile while the new column-picker UI takes over) ─────────────────────────
export function generateExecutiveDashboardCsv(_data: ExecutiveData, _sections: Record<ExecSectionKey, boolean>): string {
  // Stays around as a stub — the new CSV path uses rawRowsToCsv() via the
  // libraryDashboardRawRows endpoint. Returning empty + a comment so any
  // accidental call doesn't crash but also doesn't write garbage.
  return "# Summary CSV deprecated — use rawRowsToCsv via the dashboard UI.\n";
}
export function generateManagerDashboardCsv(_data: ManagerData, _sections: Record<ManagerSectionKey, boolean>): string {
  return "# Summary CSV deprecated — use rawRowsToCsv via the dashboard UI.\n";
}

// ─── Download helper ──────────────────────────────────────────────────────────
export function downloadCsv(text: string, filename: string): void {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => { try { URL.revokeObjectURL(url); } catch { /* ignore */ } }, 1000);
}
